import { Button } from '@chakra-ui/react';
import { ReactComponent as RandomIcon } from 'assets/icons/random.svg';
import { SettingsAccordion, Switcher } from 'modules/Editor/components/SideMenuSettings/ChartSettings';
import { SideSettingDivider } from 'modules/Editor/components/SideMenuSettings/SidemenuSettingsDivider';
import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useWidget } from 'widgets/sdk';

import { shuffleRows } from '../ChartWidget.helpers';
import { DataLabelText } from '../DataTableChartCommon/ChartSettingsLabel';
import { FontSettings } from '../DataTableChartCommon/TextStyleSettings';
import { WordCloudWidgetData } from './WordCloudWidget.types';

interface ChartStyleProps {
  enableRotate?: boolean;
}
export const ChartStyle = ({ enableRotate = false }: ChartStyleProps) => {
  const { seriesData, updateWidget, generalOptions } = useWidget<WordCloudWidgetData>();

  const { t } = useTranslation('chart_widget', { useSuspense: false, keyPrefix: 'wordcloud' });
  const label = t('randomizeButton');
  const ariaLabel = t('randomizeButtonAriaLabel');
  const { rotation } = generalOptions;

  const handleRandomize = () => {
    updateWidget({
      seriesData: shuffleRows(seriesData),
    });
  };

  const onToggleRotation = useCallback(() => {
    updateWidget({
      generalOptions: {
        rotation: !rotation,
      },
    });
  }, [rotation, updateWidget]);

  return (
    <>
      <SideSettingDivider marginY={4} />
      <Button
        onClick={handleRandomize}
        variant='action'
        width='full'
        size='sm'
        leftIcon={<RandomIcon />}
        data-testid='wordcloud-random-setting'
        aria-label={ariaLabel}
      >
        {label}
      </Button>
      {enableRotate && (
        <>
          <SideSettingDivider marginY={4} />
          <Switcher label={t('rotationLabel')} onToggle={onToggleRotation} isChecked={!!rotation} />
        </>
      )}
    </>
  );
};

export const FontStyle = memo(() => {
  const { t } = useTranslation('chart_widget', { useSuspense: false, keyPrefix: 'font' });
  const title = t('fontStyleLabel');

  return (
    <SettingsAccordion
      title={title}
      isDefaultOpen
      accordionPanelProps={{
        display: 'flex',
        gap: 3,
        flexDirection: 'column',
      }}
    >
      <DataLabelFont />
      <DataLabelText />
    </SettingsAccordion>
  );
});
FontStyle.displayName = 'FontStyle';

export const DataLabelFont = () => {
  const {
    dataLabels: {
      style: { fontFamily },
    },
    updateWidget,
  } = useWidget<WordCloudWidgetData>();

  const onFontFamilyChange = useCallback(
    (fontFamily: string) => {
      updateWidget({
        dataLabels: { style: { fontFamily } },
      });
    },
    [updateWidget],
  );

  return <FontSettings fontFamily={fontFamily} onChangeFontFamily={onFontFamilyChange} />;
};
